import React, { useState, useEffect, useCallback } from 'react';
import styles from './ActionQuestionModal.module.scss';
import { Icon } from '../../../assets/icons/Icon';

interface CheckboxOption {
  text: string;
  is_correct: boolean;
  is_exclusive?: boolean;
}

interface FollowUp {
  question: string;
  options: string[];
}

interface CheckboxWithFollowupContent {
  question: string;
  options: CheckboxOption[];
  follow_up_correct: FollowUp;
  follow_up_incorrect: FollowUp;
}

interface ActionQuestionModalProps {
  isOpen: boolean;
  onClose: () => void;
  habitName?: string;
  actionData: {
    action_id: string;
    habit_id: string;
    question_type: string;
    content: CheckboxWithFollowupContent;
  };
  onSubmit: (data: {
    action_id: string;
    selected_options: number[];
    follow_up_answer?: string;
  }) => void;
  isLoading?: boolean;
  showPositiveImpacts?: boolean;
  onAddImpacts?: () => void;
}

export const ActionQuestionModal: React.FC<ActionQuestionModalProps> = ({
  isOpen,
  onClose,
  habitName,
  actionData,
  onSubmit,
  isLoading = false,
  showPositiveImpacts = false,
  onAddImpacts,
}) => {
  const [selectedOptions, setSelectedOptions] = useState<number[]>([]);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [selectedFollowUp, setSelectedFollowUp] = useState<string | null>(null);
  const [showCompletion, setShowCompletion] = useState(false);

  const content = actionData.content;
  
  // Format today's date
  const today = new Date();
  const formattedDate = today.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      setSelectedOptions([]);
      setShowFollowUp(false);
      setSelectedFollowUp(null);
      setShowCompletion(false);
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, handleKeyDown]);

  const handleOptionToggle = (index: number) => {
    const option = content.options[index];

    if (option.is_exclusive) {
      // Exclusive option clears others and selects only this one
      setSelectedOptions([index]);
    } else {
      // Remove any exclusive options when selecting non-exclusive
      const exclusiveIndices = content.options
        .map((opt, idx) => (opt.is_exclusive ? idx : -1))
        .filter((idx) => idx !== -1);

      setSelectedOptions((prev) => {
        const withoutExclusive = prev.filter((idx) => !exclusiveIndices.includes(idx));
        if (withoutExclusive.includes(index)) {
          return withoutExclusive.filter((idx) => idx !== index);
        } else {
          return [...withoutExclusive, index];
        }
      });
    }
  };

  const handleNext = () => {
    if (!showFollowUp) {
      setShowFollowUp(true);
    } else {
      // Submit the response
      onSubmit({
        action_id: actionData.action_id,
        selected_options: selectedOptions,
        follow_up_answer: selectedFollowUp || undefined,
      });
      // Show completion screen
      setShowCompletion(true);
    }
  };

  const handleAddImpacts = () => {
    if (onAddImpacts) {
      onAddImpacts();
    }
    onClose();
  };

  // Determine which follow-up to show based on selected options
  const hasIncorrectSelection = selectedOptions.some(
    (idx) => content.options[idx]?.is_correct === false
  );
  const followUp = hasIncorrectSelection ? content.follow_up_incorrect : content.follow_up_correct;

  // Determine the title based on correctness
  const followUpTitle = hasIncorrectSelection
    ? 'Did not have an opportunity'
    : content.options.some((opt, idx) => selectedOptions.includes(idx) && opt.text)
    ? `Great, that's secure thinking in action`
    : 'Follow up question';

  if (!isOpen) return null;

  return (
    <div className={styles['action-question-overlay']} onClick={onClose}>
      <div
        className={styles['action-question-modal']}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="action-question-title"
        tabIndex={-1}
      >
        {!showCompletion && (
          <div className={styles['action-question-modal__header']}>
            <h2 id="action-question-title" className={styles['action-question-modal__title']}>
              {showFollowUp ? followUpTitle : 'Daily reflection'}
            </h2>
            <button
              className={styles['action-question-modal__close-button']}
              onClick={onClose}
              aria-label="Close modal"
              type="button"
            >
              <Icon iconName="CrossIcon" width="1.2rem" height="1.2rem" />
            </button>
          </div>
        )}

        {habitName && !showCompletion && (
          <div className={styles['action-question-modal__habit-info']}>
            <div className={styles['action-question-modal__habit-icon']}>
              <Icon iconName="SelectUserIcon" width="1.5rem" height="1.5rem" />
            </div>
            <div className={styles['action-question-modal__habit-details']}>
              <span className={styles['action-question-modal__habit-name']}>{habitName}</span>
              <span className={styles['action-question-modal__habit-date']}>Date: {formattedDate}</span>
            </div>
          </div>
        )}

        {showCompletion ? (
          <>
            <div className={styles['action-question-modal__header']}>
              <h2 className={styles['action-question-modal__title']}>
                Well done! Come back tomorrow
              </h2>
              <button
                className={styles['action-question-modal__close-button']}
                onClick={onClose}
                aria-label="Close modal"
                type="button"
              >
                <Icon iconName="CrossIcon" width="1.2rem" height="1.2rem" />
              </button>
            </div>
            <div className={styles['action-question-modal__completion']}>
              <div className={styles['action-question-modal__completion-icon']}>
                <Icon iconName="EndOfActionHabitIcon" width="111" height="111" />
              </div>
              <p className={styles['action-question-modal__completion-message']}>
                Awareness begins with noticing. Tomorrow, try to catch one moment where you stop to think before clicking, sharing, or replying.
              </p>
            </div>
            <div className={styles['action-question-modal__completion-footer']}>
              {showPositiveImpacts && (
                <>
                  <span className={styles['action-question-modal__impacts-question']}>
                    Have you noticed any positive impacts?
                  </span>
                  <button
                    className={styles['action-question-modal__add-impacts-button']}
                    onClick={handleAddImpacts}
                    type="button"
                  >
                    <Icon iconName="PlusIcon" width="1rem" height="1rem" />
                    Add impacts
                  </button>
                </>
              )}
              <button
                className={styles['action-question-modal__done-button']}
                onClick={onClose}
                type="button"
              >
                Close
              </button>
            </div>
          </>
        ) : (
          <>
            <div className={styles['action-question-modal__content']}>
              {!showFollowUp ? (
                <>
                  <p className={styles['action-question-modal__question']}>{content.question}</p>
                  <div className={styles['action-question-modal__options']}>
                    {content.options.map((option, index) => (
                      <label
                        key={index}
                        className={`${styles['action-question-modal__option']} ${
                          selectedOptions.includes(index)
                            ? styles['action-question-modal__option--selected']
                            : ''
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedOptions.includes(index)}
                          onChange={() => handleOptionToggle(index)}
                          className={styles['action-question-modal__checkbox']}
                        />
                        <span className={styles['action-question-modal__option-text']}>
                          {option.text}
                        </span>
                      </label>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <p className={styles['action-question-modal__question']}>{followUp.question}</p>
                  <div className={styles['action-question-modal__options']}>
                    {followUp.options.map((option, index) => (
                      <label
                        key={index}
                        className={`${styles['action-question-modal__option']} ${
                          selectedFollowUp === option
                            ? styles['action-question-modal__option--selected']
                            : ''
                        }`}
                      >
                        <input
                          type="radio"
                          name="followup"
                          checked={selectedFollowUp === option}
                          onChange={() => setSelectedFollowUp(option)}
                          className={styles['action-question-modal__radio']}
                        />
                        <span className={styles['action-question-modal__option-text']}>{option}</span>
                      </label>
                    ))}
                  </div>
                </>
              )}
            </div>

            <div className={styles['action-question-modal__footer']}>
              <button
                className={styles['action-question-modal__cancel-button']}
                onClick={showFollowUp ? () => setShowFollowUp(false) : onClose}
                type="button"
              >
                {showFollowUp ? 'Back' : 'Cancel'}
              </button>
              <button
                className={styles['action-question-modal__next-button']}
                onClick={handleNext}
                disabled={
                  (!showFollowUp && selectedOptions.length === 0) ||
                  (showFollowUp && !selectedFollowUp) ||
                  isLoading
                }
                type="button"
              >
                {isLoading ? 'Loading...' : 'Next'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

